import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { AuthContext } from './AuthProvider';
import api from '../utils/api';
import { toast } from 'react-toastify';

const PlanContext = createContext();

// Optimized configurations for minimal requests
const CONFIG = {
  CACHE_DURATION: 4 * 60 * 60 * 1000, // 4 hours - refresh permissions every 4 hours
  REFRESH_ON_PLAN_CHANGE: true,
  REFRESH_ON_COMPANY_UPDATE: true
};

// Global shared state
let globalPlanState = {
  data: null,
  timestamp: null,
  loading: false,
  subscribers: new Set(),
  pendingSync: null,
  lastRefresh: null,
  localUsage: {
    jobPostings: 0,
    candidateViews: 0,
    teamMembers: 0,
    lastSync: null
  },
  // Control when to revalidate
  needsRevalidation: false,
  criticalActionsCount: 0
};

// Helper function to create plan data from API response
const createPlanDataFromApi = (apiResponse) => {
  if (!apiResponse?.data?.attributes) {
    return null;
  }

  const { attributes } = apiResponse.data;

  return {
    plan: attributes.plan,
    permissions: attributes.permissions,
    limits: attributes.limits,
    usage: attributes.usage,
    // Bloqueio global da empresa (inadimplência / trial expirado)
    blocked: attributes.blocked ?? false,
    blockReason: attributes.blockReason ?? null,
    subscriptionStatus: attributes.subscriptionStatus ?? null,
    lastUpdate: new Date().toISOString(),
  };
};

export const PlanProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [planData, setPlanData] = useState(globalPlanState.data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const retryCountRef = useRef(0);

  // Register component as subscriber
  useEffect(() => {
    const updateState = (newData) => {
      // Always update state if we have data
      if (newData) {
        setPlanData(newData);
        setLoading(globalPlanState.loading);
      }
    };

    globalPlanState.subscribers.add(updateState);

    // Sync local state with global state immediately
    if (globalPlanState.data) {
      setPlanData(globalPlanState.data);
    }

    return () => {
      globalPlanState.subscribers.delete(updateState);
    };
  }, []);

  // Additional effect to sync state when global state changes
  useEffect(() => {
    if (globalPlanState.data && !planData) {
      setPlanData(globalPlanState.data);
    }
  }, [globalPlanState.data, planData]);

  // Notify all subscribers
  const notifySubscribers = (newData) => {
    globalPlanState.data = newData;
    globalPlanState.timestamp = Date.now();
    globalPlanState.lastRefresh = Date.now();
    globalPlanState.needsRevalidation = false;

    globalPlanState.subscribers.forEach(callback => {
      callback(newData);
    });
  };

  // Check if cache is valid (much more permissive)
  const isCacheValid = () => {
    const hasData = !!globalPlanState.data;
    const hasTimestamp = !!globalPlanState.timestamp;
    const cacheAge = globalPlanState.timestamp ? Date.now() - globalPlanState.timestamp : null;

    // If data is fallback trial data, consider cache invalid after 5 minutes
    const isFallbackData = globalPlanState.data?.plan?.name === 'trial' &&
                          globalPlanState.data?.plan?.trial === true;
    const maxAge = isFallbackData ? 5 * 60 * 1000 : CONFIG.CACHE_DURATION; // 5 min for fallback, normal for API
    const isAgeValid = cacheAge && cacheAge < maxAge;
    const noRevalidation = !globalPlanState.needsRevalidation;

    return hasData && hasTimestamp && isAgeValid && noRevalidation;
  };

  // Fetch plan data from backend API (only when necessary)
  const fetchPlanData = async (force = false, reason = 'manual') => {
    // If no user, don't fetch
    if (!user?.authToken) {
      return null;
    }

    // Use cache if valid and not forced
    if (!force && isCacheValid()) {
      setPlanData(globalPlanState.data);
      return globalPlanState.data;
    }

    // Avoid duplicate requests
    if (globalPlanState.loading) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!globalPlanState.loading) {
            clearInterval(checkInterval);
            resolve(globalPlanState.data);
          }
        }, 100);
      });
    }

    try {
      globalPlanState.loading = true;
      setLoading(true);

      // Call the backend API to get permissions
      let response;
      try {
        response = await api.get('/companies/permissions');
      } catch (apiError) {
        throw apiError;
      }

      const planData = createPlanDataFromApi(response.data);

      if (!planData) {
        throw new Error('Invalid API response format');
      }

      // Update global state with API data
      globalPlanState.data = planData;
      globalPlanState.timestamp = Date.now();
      globalPlanState.lastRefresh = Date.now();
      globalPlanState.needsRevalidation = false;

      notifySubscribers(planData);
      setError(null);
      retryCountRef.current = 0;

      return planData;

    } catch (err) {
      setError('Error validating permissions');
      if (force) {
        toast.error('Error validating your plan. Please try again.');
      }

      // Return existing data on error instead of creating fallback
      return globalPlanState.data;
    } finally {
      globalPlanState.loading = false;
      setLoading(false);
    }
  };

  // Update local usage (optimistic update, no backend sync)
  const updateLocalUsage = (feature, increment = 1) => {
    const now = Date.now();

    // Update local usage without backend sync
    switch (feature) {
      case 'createJobPosting':
        globalPlanState.localUsage.jobPostings += increment;
        break;
      case 'viewCandidateDetails':
        globalPlanState.localUsage.candidateViews += increment;
        break;
      case 'addTeamMember':
        globalPlanState.localUsage.teamMembers += increment;
        break;
      case 'favoriteTalent':
        // For favorites, we just track locally, backend will update on API call
        break;
    }

    globalPlanState.localUsage.lastSync = now;

    // Update plan data locally for immediate UX
    if (globalPlanState.data) {
      const updatedData = { ...globalPlanState.data };
      if (updatedData.usage) {
        switch (feature) {
          case 'createJobPosting':
            updatedData.usage.job_postings = (updatedData.usage.job_postings || 0) + increment;
            break;
          case 'viewCandidateDetails':
            updatedData.usage.candidate_views = (updatedData.usage.candidate_views || 0) + increment;
            break;
          case 'addTeamMember':
            updatedData.usage.team_members = (updatedData.usage.team_members || 0) + increment;
            break;
          case 'favoriteTalent':
            updatedData.usage.favorite_talents = (updatedData.usage.favorite_talents || 0) + increment;
            break;
        }
      }
      notifySubscribers(updatedData);
    }

    // No backend sync - just update local state
    // Backend sync will happen only when explicitly needed
  };

  // Revalidate plan for critical actions (only when necessary)
  const revalidateForCriticalAction = async (feature) => {
    // Only revalidate for plan-changing actions, not for regular usage
    const isPlanChangingAction = ['planUpgrade', 'planDowngrade', 'planCancellation'].includes(feature);

    if (isPlanChangingAction && CONFIG.REFRESH_ON_PLAN_CHANGE) {
      return await fetchPlanData(true, `plan-change-${feature}`);
    }

    // For regular actions, just return cached data
    return globalPlanState.data;
  };

  // Mark plan for revalidation (for external changes)
  const markForRevalidation = (reason = 'external-change') => {
    globalPlanState.needsRevalidation = true;
  };

  // Update permissions when company data changes
  const updatePermissionsOnCompanyChange = async (companyData) => {
    // Check if plan-related data changed
    const currentPlan = globalPlanState.data?.plan?.name;
    const newPlan = companyData?.plan?.name;

    if (currentPlan !== newPlan && CONFIG.REFRESH_ON_COMPANY_UPDATE) {
      // Plan changed, refresh permissions
      return await fetchPlanData(true, 'company-plan-change');
    }

    // No plan change, just mark for potential revalidation
    if (CONFIG.REFRESH_ON_COMPANY_UPDATE) {
      markForRevalidation('company-data-update');
    }

    return globalPlanState.data;
  };

  // Clear cache and data
  const clearPlanData = () => {
    globalPlanState.data = null;
    globalPlanState.timestamp = null;
    globalPlanState.lastRefresh = null;
    globalPlanState.needsRevalidation = false;
    globalPlanState.criticalActionsCount = 0;
    globalPlanState.localUsage = {
      jobPostings: 0,
      candidateViews: 0,
      teamMembers: 0,
      lastSync: null
    };
    notifySubscribers(null);
  };

  // Load initial data (only on login)
  useEffect(() => {
    if (user?.authToken) {
      // Only fetch if no data exists or if user plan changed
      if (!globalPlanState.data) {
        // First time login - fetch permissions
        fetchPlanData(false, 'first-login');
      } else if (globalPlanState.needsRevalidation) {
        // Only revalidate if explicitly needed
        fetchPlanData(false, 'revalidation');
      } else {
        // Use existing data
        setPlanData(globalPlanState.data);
      }
    } else {
      clearPlanData();
    }
  }, [user?.authToken]); // Removed user?.plan?.trial to avoid unnecessary fetches

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup any pending operations
    };
  }, []);

  const value = {
    planData,
    loading,
    error,
    fetchPlanData,
    updateLocalUsage,
    clearPlanData,
    isCacheValid,
    revalidateForCriticalAction,
    markForRevalidation,
    updatePermissionsOnCompanyChange,
    // Debug metrics
    getCacheInfo: () => ({
      hasCache: !!globalPlanState.data,
      cacheAge: globalPlanState.timestamp ? Date.now() - globalPlanState.timestamp : null,
      isValid: isCacheValid(),
      subscribers: globalPlanState.subscribers.size,
      localUsage: globalPlanState.localUsage,
      lastRefresh: globalPlanState.lastRefresh,
      needsRevalidation: globalPlanState.needsRevalidation,
      criticalActionsCount: globalPlanState.criticalActionsCount
    })
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

// Custom hook to use the context
export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export default PlanContext;
